export type ExecuteAggRequestArgs = {
	url: string;
	headers: Record<string, string>;
	body: unknown;
	timeoutMs?: number;
	/** Optional external signal to abort the request (e.g. when the user clicks Undo). */
	signal?: AbortSignal;
};

export async function executeAggRequest(args: ExecuteAggRequestArgs): Promise<any> {
	const { url, headers, body, timeoutMs = 30000, signal: externalSignal } = args;

	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), timeoutMs);

	// If an external signal is provided, abort the internal controller when it fires.
	let onExternalAbort: (() => void) | undefined;
	if (externalSignal) {
		if (externalSignal.aborted) {
			controller.abort();
		} else {
			onExternalAbort = () => controller.abort();
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners -- AbortSignal is not a DOM EventTarget; bind-event-listener does not support it
			externalSignal.addEventListener('abort', onExternalAbort);
		}
	}

	try {
		const res = await fetch(url, {
			method: 'POST',
			credentials: 'include',
			headers,
			body: JSON.stringify(body),
			signal: controller.signal,
		});

		const ct = res.headers.get('content-type') || '';
		if (ct.includes('application/json')) {
			return await res.json();
		}
		return await res.text();
	} finally {
		clearTimeout(timeout);
		if (onExternalAbort && externalSignal) {
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners -- AbortSignal is not a DOM EventTarget; bind-event-listener does not support it
			externalSignal.removeEventListener('abort', onExternalAbort);
		}
	}
}

export function extractOperationName(query: string): string | undefined {
	const match = query.match(/(?:query|mutation)\s+(\w+)/);
	return match ? match[1] : undefined;
}
